/**
 * SearchBar Component
 * Search input for filtering orders by order number, customer name, or email
 * Features debounced input to reduce API calls
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 2
 */

import * as React from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useDebounce } from '../hooks/useDebounce';

// ============================================
// INTERFACE
// ============================================

export interface SearchBarProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  placeholder?: string;
  className?: string;
}

// ============================================
// COMPONENT
// ============================================

export const SearchBar = React.memo(function SearchBar({
  searchTerm,
  onSearchChange,
  placeholder = 'Search by order #, customer name, or email...',
  className,
}: SearchBarProps) {
  // Local state for immediate input feedback
  const [localValue, setLocalValue] = React.useState(searchTerm);

  // Debounce the search term to reduce API calls
  const debouncedValue = useDebounce(localValue, 300);

  // Track if this is the first render to avoid initial API call
  const isFirstRender = React.useRef(true);

  // Update parent when debounced value changes
  React.useEffect(() => {
    // Skip the first render to avoid unnecessary API call on mount
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Always call onSearchChange when debouncedValue changes (after first render)
    console.log('[SearchBar] Debounced value changed, calling onSearchChange:', debouncedValue);
    onSearchChange(debouncedValue);
  }, [debouncedValue, onSearchChange]);

  // Sync local value when searchTerm prop changes externally (e.g., reset)
  React.useEffect(() => {
    if (searchTerm === '' && localValue !== '') {
      setLocalValue('');
    }
  }, [searchTerm]);

  // Handle input change
  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  // Handle clear button
  const handleClear = React.useCallback(() => {
    setLocalValue('');
    onSearchChange('');
  }, [onSearchChange]);

  // Handle keyboard shortcuts
  const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      handleClear();
    }
  }, [handleClear]);

  return (
    <div className={cn('relative w-full', className)}>
      <div className="relative flex items-center">
        {/* Search Icon */}
        <Search
          className="absolute left-3 h-4 w-4 text-muted-foreground pointer-events-none"
          aria-hidden="true"
        />

        {/* Input Field */}
        <Input
          type="text"
          value={localValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="pl-9 pr-9"
          aria-label="Search orders"
          aria-describedby="search-hint"
        />

        {/* Clear Button */}
        {localValue && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClear}
            className="absolute right-1 h-7 w-7 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Screen reader hint */}
      <span id="search-hint" className="sr-only">
        Search will filter orders as you type. Press Escape to clear.
      </span>
    </div>
  );
});
